import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { authAPI } from '../../services/api';
import {
    UserIcon,
    PlusIcon,
    PencilIcon,
    TrashIcon,
    MagnifyingGlassIcon,
    XMarkIcon,
    CheckIcon,
    EyeIcon,
    EyeSlashIcon,
    PowerIcon,
    ShieldCheckIcon
} from '@heroicons/react/24/outline';

const UserManagement = () => {
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showForm, setShowForm] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [formData, setFormData] = useState({
        name: '',
        email: '',
        password: '',
        role: 'user',
        whatsappCredits: 0,
        smsCredits: 0,
        voiceCallCredits: 0,
        smsRate: 1,
        whatsappRate: 1,
        voiceCallRate: 1
    });
    const [formErrors, setFormErrors] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    const [deleting, setDeleting] = useState(false);
    const [deleteConfirmText, setDeleteConfirmText] = useState('');
    const [showDisableModal, setShowDisableModal] = useState(false);
    const [userToDisable, setUserToDisable] = useState(null);
    const [disabling, setDisabling] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [filters, setFilters] = useState({
        search: '',
        role: '',
        status: '',
        page: 1,
        limit: 10
    });
    const [pagination, setPagination] = useState({});

    useEffect(() => {
        fetchUsers();
    }, [filters]);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            const response = await authAPI.getUsers(filters);
            setUsers(response.data.data.users);
            setPagination(response.data.data.pagination);
        } catch (error) {
            toast.error('Failed to load users');
            console.error('Users fetch error:', error);
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setFormData({
            name: '',
            email: '',
            password: '',
            role: 'user',
            whatsappCredits: 0,
            smsCredits: 0,
            voiceCallCredits: 0,
            smsRate: 1,
            whatsappRate: 1,
            voiceCallRate: 1
        });
        setFormErrors({});
        setEditingUser(null);
        setShowPassword(false);
    };

    const handleShowForm = () => {
        resetForm();
        setShowForm(true);
    };

    const handleCloseForm = () => {
        setShowForm(false);
        resetForm();
    };

    const togglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const handleEditUser = (user) => {
        setEditingUser(user);
        setFormData({
            name: user.name,
            email: user.email,
            password: '', // Don't populate password for security
            role: user.role,
            whatsappCredits: user.whatsappCredits || 0,
            smsCredits: user.smsCredits || 0,
            voiceCallCredits: user.voiceCallCredits || 0,
            smsRate: user.creditRates?.smsRate || 1,
            whatsappRate: user.creditRates?.whatsappRate || 1,
            voiceCallRate: user.creditRates?.voiceCallRate || 1
        });
        setFormErrors({});
        setShowForm(true);
    };

    const validateForm = () => {
        const errors = {};

        if (!formData.name.trim()) {
            errors.name = 'Username is required';
        } else if (formData.name.trim().length < 2) {
            errors.name = 'Username must be at least 2 characters';
        }

        if (!formData.email.trim()) {
            errors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            errors.email = 'Email is invalid';
        }

        if (!editingUser && (!formData.password || formData.password.trim() === '')) {
            errors.password = 'Password is required';
        } else if (formData.password && formData.password.trim().length > 0 && formData.password.trim().length < 6) {
            errors.password = 'Password must be at least 6 characters';
        }

        if (formData.whatsappCredits < 0) {
            errors.whatsappCredits = 'WhatsApp credits cannot be negative';
        }

        if (formData.smsCredits < 0) {
            errors.smsCredits = 'SMS credits cannot be negative';
        }

        if (formData.voiceCallCredits < 0) {
            errors.voiceCallCredits = 'Voice call credits cannot be negative';
        }

        if (formData.smsRate < 0.1) {
            errors.smsRate = 'SMS rate must be at least 0.1 credits per phone number';
        }

        if (formData.whatsappRate < 0.1) {
            errors.whatsappRate = 'WhatsApp rate must be at least 0.1 credits per phone number';
        }

        if (formData.voiceCallRate < 0.1) {
            errors.voiceCallRate = 'Voice call rate must be at least 0.1 credits per phone number';
        }

        setFormErrors(errors);
        return Object.keys(errors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setSubmitting(true);
        try {
            const submitData = { ...formData };

            // Don't send empty password for updates
            if (editingUser && (!submitData.password || submitData.password.trim() === '')) {
                delete submitData.password;
            }

            if (editingUser) {
                await authAPI.updateUser(editingUser._id, submitData);
                toast.success('User updated successfully');
            } else {
                await authAPI.createUser(submitData);
                toast.success('User created successfully');
            }

            handleCloseForm();
            fetchUsers();
        } catch (error) {
            const message = error.response?.data?.message || 'Operation failed';
            toast.error(message);
            console.error('Form submit error:', error);
        } finally {
            setSubmitting(false);
        }
    };

    const handleDeleteUser = (user) => {
        setUserToDelete(user);
        setShowDeleteModal(true);
    };

    const handleConfirmDelete = async () => {
        if (!userToDelete) return;

        setDeleting(true);
        try {
            await authAPI.deleteUser(userToDelete._id);
            toast.success('User deleted successfully');
            fetchUsers();
            setShowDeleteModal(false);
            setUserToDelete(null);
            setDeleteConfirmText('');
        } catch (error) {
            const message = error.response?.data?.message || 'Failed to delete user';
            toast.error(message);
            console.error('Delete user error:', error);
        } finally {
            setDeleting(false);
        }
    };

    const handleCancelDelete = () => {
        setShowDeleteModal(false);
        setUserToDelete(null);
        setDeleteConfirmText('');
    };

    const handleToggleUserStatus = async (user) => {
        // If user is active (we're about to disable), show confirmation modal
        if (user.isActive) {
            setUserToDisable(user);
            setShowDisableModal(true);
            return;
        }

        // If user is inactive (we're enabling), do it immediately
        try {
            await authAPI.toggleUserStatus(user._id);
            toast.success('User enabled successfully');
            fetchUsers();
        } catch (error) {
            const message = error.response?.data?.message || 'Failed to enable user';
            toast.error(message);
            console.error('Enable user error:', error);
        }
    };

    const handleConfirmDisable = async () => {
        if (!userToDisable) return;

        setDisabling(true);
        try {
            await authAPI.toggleUserStatus(userToDisable._id);
            toast.success('User disabled successfully');
            fetchUsers();
            setShowDisableModal(false);
            setUserToDisable(null);
        } catch (error) {
            const message = error.response?.data?.message || 'Failed to disable user';
            toast.error(message);
            console.error('Disable user error:', error);
        } finally {
            setDisabling(false);
        }
    };

    const handleCancelDisable = () => {
        setShowDisableModal(false);
        setUserToDisable(null);
    };

    const handleTogglePii = async (user) => {
        try {
            await authAPI.togglePiiProtection(user._id);
            toast.success(`PII protection ${user.piiProtection ? 'disabled' : 'enabled'} for ${user.name}`);
            fetchUsers();
        } catch (error) {
            const message = error.response?.data?.message || 'Failed to toggle PII protection';
            toast.error(message);
        }
    };

    const handleInputChange = (e) => {
        const { name, value, type } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'number' ? parseInt(value) || 0 : value
        }));

        // Clear error when user starts typing
        if (formErrors[name]) {
            setFormErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({
            ...prev,
            [key]: value,
            page: 1
        }));
    };

    const handlePageChange = (newPage) => {
        setFilters(prev => ({
            ...prev,
            page: newPage
        }));
    };

    return (
        <>
            <div className="space-y-6">
                {/* Header */}
                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-bold text-soft-900">User Management</h1>
                        <p className="text-soft-600 mt-1">
                            Manage users, roles, and communication credits
                        </p>
                    </div>
                    <button
                        onClick={handleShowForm}
                        className="btn-primary flex items-center space-x-2"
                    >
                        <PlusIcon className="h-5 w-5" />
                        <span>Add User</span>
                    </button>
                </div>

                {/* Filters */}
                <div className="card">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                        <div className="relative">
                            <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-soft-400" />
                            <input
                                type="text"
                                placeholder="Search users..."
                                className="input-field pl-10"
                                value={filters.search}
                                onChange={(e) => handleFilterChange('search', e.target.value)}
                            />
                        </div>
                        <select
                            className="input-field"
                            value={filters.role}
                            onChange={(e) => handleFilterChange('role', e.target.value)}
                        >
                            <option value="">All Roles</option>
                            <option value="admin">Admin</option>
                            <option value="user">User</option>
                        </select>
                        <select
                            className="input-field"
                            value={filters.status}
                            onChange={(e) => handleFilterChange('status', e.target.value)}
                        >
                            <option value="">All Status</option>
                            <option value="active">Active Only</option>
                            <option value="inactive">Inactive Only</option>
                        </select>
                        <select
                            className="input-field"
                            value={filters.limit}
                            onChange={(e) => handleFilterChange('limit', parseInt(e.target.value))}
                        >
                            <option value={10}>10 per page</option>
                            <option value={25}>25 per page</option>
                            <option value={50}>50 per page</option>
                        </select>
                    </div>
                </div>

                {/* Users Table */}
                <div className="card">
                    {loading ? (
                        <div className="text-center py-12">
                            <div className="loading-spinner mx-auto mb-4"></div>
                            <p className="text-soft-600">Loading users...</p>
                        </div>
                    ) : users.length > 0 ? (
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-soft-200">
                                <thead className="bg-soft-50">
                                    <tr>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-soft-500 uppercase tracking-wider">
                                            User
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-soft-500 uppercase tracking-wider">
                                            Role
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-soft-500 uppercase tracking-wider">
                                            WhatsApp
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-soft-500 uppercase tracking-wider">
                                            SMS
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-soft-500 uppercase tracking-wider">
                                            Voice
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-soft-500 uppercase tracking-wider">
                                            Status
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-soft-500 uppercase tracking-wider">
                                            PII Protection
                                        </th>
                                        <th className="px-6 py-3 text-left text-xs font-medium text-soft-500 uppercase tracking-wider">
                                            Created
                                        </th>
                                        <th className="px-6 py-3 text-right text-xs font-medium text-soft-500 uppercase tracking-wider">
                                            Actions
                                        </th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-soft-200">
                                    {users.map((user) => (
                                        <tr key={user._id} className={`hover:bg-soft-50 ${!user.isActive ? 'opacity-60 bg-soft-25' : ''}`}>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <div className="flex items-center">
                                                    <div className="h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center">
                                                        <UserIcon className="h-5 w-5 text-primary-600" />
                                                    </div>
                                                    <div className="ml-4">
                                                        <div className="text-sm font-medium text-soft-900">
                                                            {user.name}
                                                        </div>
                                                        <div className="text-sm text-soft-500">
                                                            {user.email}
                                                        </div>
                                                    </div>
                                                </div>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${user.role === 'admin'
                                                    ? 'bg-purple-100 text-purple-800'
                                                    : 'bg-blue-100 text-blue-800'
                                                    }`}>
                                                    {user.role.toUpperCase()}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-soft-900">
                                                {user.whatsappCredits?.toLocaleString() || 0}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-soft-900">
                                                {user.smsCredits?.toLocaleString() || 0}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-soft-900">
                                                {user.voiceCallCredits?.toLocaleString() || 0}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${user.isActive
                                                    ? 'bg-success-100 text-success-800'
                                                    : 'bg-error-100 text-error-800'
                                                    }`}>
                                                    {user.isActive ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap">
                                                <button
                                                    onClick={() => handleTogglePii(user)}
                                                    title={user.piiProtection ? 'PII Protection ON — click to disable' : 'PII Protection OFF — click to enable'}
                                                    className={`relative inline-flex items-center h-6 w-11 rounded-full transition-colors focus:outline-none ${user.piiProtection ? 'bg-primary-600' : 'bg-soft-300'}`}
                                                >
                                                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${user.piiProtection ? 'translate-x-6' : 'translate-x-1'}`} />
                                                </button>
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-sm text-soft-500">
                                                {new Date(user.createdAt).toLocaleDateString()}
                                            </td>
                                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                                <div className="flex items-center justify-end space-x-2">
                                                    <button
                                                        onClick={() => handleToggleUserStatus(user)}
                                                        className={`p-1 ${user.isActive
                                                            ? 'text-warning-600 hover:text-warning-900'
                                                            : 'text-success-600 hover:text-success-900'
                                                            }`}
                                                        title={user.isActive ? 'Disable User' : 'Enable User'}
                                                    >
                                                        <PowerIcon className="h-4 w-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleEditUser(user)}
                                                        className="text-primary-600 hover:text-primary-900 p-1"
                                                        title="Edit User"
                                                    >
                                                        <PencilIcon className="h-4 w-4" />
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteUser(user)}
                                                        className="text-error-600 hover:text-error-900 p-1"
                                                        title="Delete User"
                                                    >
                                                        <TrashIcon className="h-4 w-4" />
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="text-center py-12">
                            <UserIcon className="mx-auto h-12 w-12 text-soft-400" />
                            <h3 className="mt-2 text-sm font-medium text-soft-900">No users found</h3>
                            <p className="mt-1 text-sm text-soft-500">
                                {filters.search || filters.role || filters.status
                                    ? 'No users match your current filters.'
                                    : 'Get started by creating your first user.'}
                            </p>
                            {!filters.search && !filters.role && !filters.status && (
                                <div className="mt-6">
                                    <button
                                        onClick={handleShowForm}
                                        className="btn-primary inline-flex items-center space-x-2"
                                    >
                                        <PlusIcon className="h-5 w-5" />
                                        <span>Add User</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Pagination */}
                    {pagination.pages > 1 && (
                        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-soft-200 sm:px-6">
                            <div className="flex-1 flex justify-between sm:hidden">
                                <button
                                    onClick={() => handlePageChange(pagination.current - 1)}
                                    disabled={pagination.current === 1}
                                    className="relative inline-flex items-center px-4 py-2 border border-soft-300 text-sm font-medium rounded-md text-soft-700 bg-white hover:bg-soft-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Previous
                                </button>
                                <button
                                    onClick={() => handlePageChange(pagination.current + 1)}
                                    disabled={pagination.current === pagination.pages}
                                    className="ml-3 relative inline-flex items-center px-4 py-2 border border-soft-300 text-sm font-medium rounded-md text-soft-700 bg-white hover:bg-soft-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    Next
                                </button>
                            </div>
                            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                                <div>
                                    <p className="text-sm text-soft-700">
                                        Showing page <span className="font-medium">{pagination.current}</span> of{' '}
                                        <span className="font-medium">{pagination.pages}</span>
                                        {' '}({pagination.total} total users)
                                    </p>
                                </div>
                                <div>
                                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                                        <button
                                            onClick={() => handlePageChange(pagination.current - 1)}
                                            disabled={pagination.current === 1}
                                            className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-soft-300 bg-white text-sm font-medium text-soft-500 hover:bg-soft-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            Previous
                                        </button>
                                        <button
                                            onClick={() => handlePageChange(pagination.current + 1)}
                                            disabled={pagination.current === pagination.pages}
                                            className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-soft-300 bg-white text-sm font-medium text-soft-500 hover:bg-soft-50 disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            Next
                                        </button>
                                    </nav>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* User Form Modal */}
            {showForm && (
                <div className="modal-overlay fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                    <div className="modal-content bg-white rounded-xl shadow-xl max-w-md w-full max-h-[80vh] overflow-y-auto">
                        <div className="p-6">
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-xl font-bold text-soft-900">
                                    {editingUser ? 'Edit User' : 'Add New User'}
                                </h2>
                                <button
                                    onClick={handleCloseForm}
                                    className="text-soft-400 hover:text-soft-600"
                                >
                                    <XMarkIcon className="h-6 w-6" />
                                </button>
                            </div>

                            <form onSubmit={handleSubmit} className="space-y-4" autoComplete="off">
                                <div>
                                    <label className="block text-sm font-medium text-soft-700 mb-2">
                                        Username *
                                    </label>
                                    <input
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleInputChange}
                                        className={`input-field ${formErrors.name ? 'border-error-500' : ''}`}
                                        placeholder="Enter username"
                                        autoComplete="off"
                                        autoCorrect="off"
                                        autoCapitalize="off"
                                        spellCheck="false"
                                    />
                                    {formErrors.name && (
                                        <p className="text-error-600 text-sm mt-1">{formErrors.name}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-soft-700 mb-2">
                                        Email *
                                    </label>
                                    <input
                                        type="email"
                                        name="email"
                                        value={formData.email}
                                        onChange={handleInputChange}
                                        className={`input-field ${formErrors.email ? 'border-error-500' : ''}`}
                                        placeholder="Enter email address"
                                        autoComplete="off"
                                        autoCorrect="off"
                                        autoCapitalize="off"
                                        spellCheck="false"
                                    />
                                    {formErrors.email && (
                                        <p className="text-error-600 text-sm mt-1">{formErrors.email}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-soft-700 mb-2">
                                        Password {!editingUser && '*'}
                                    </label>
                                    <div className="relative">
                                        <input
                                            type={showPassword ? "text" : "password"}
                                            name="password"
                                            value={formData.password}
                                            onChange={handleInputChange}
                                            className={`input-field pr-10 ${formErrors.password ? 'border-error-500' : ''}`}
                                            placeholder={editingUser ? "Leave blank to keep current password" : "Enter password"}
                                            autoComplete="new-password"
                                            autoCorrect="off"
                                            autoCapitalize="off"
                                            spellCheck="false"
                                        />
                                        {formData.password && (
                                            <button
                                                type="button"
                                                onClick={togglePasswordVisibility}
                                                className="absolute inset-y-0 right-0 pr-3 flex items-center text-soft-400 hover:text-soft-600 focus:outline-none"
                                            >
                                                {showPassword ? (
                                                    <EyeSlashIcon className="h-5 w-5" />
                                                ) : (
                                                    <EyeIcon className="h-5 w-5" />
                                                )}
                                            </button>
                                        )}
                                    </div>
                                    {formErrors.password && (
                                        <p className="text-error-600 text-sm mt-1">{formErrors.password}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-soft-700 mb-2">
                                        Role *
                                    </label>
                                    <select
                                        name="role"
                                        value={formData.role}
                                        onChange={handleInputChange}
                                        className="input-field"
                                    >
                                        <option value="user">User</option>
                                        <option value="admin">Admin</option>
                                    </select>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-soft-700 mb-2">
                                        Communication Credits
                                    </label>
                                    <div className="grid grid-cols-3 gap-3">
                                        <div>
                                            <label className="block text-xs text-soft-600 mb-1">
                                                WhatsApp
                                            </label>
                                            <input
                                                type="number"
                                                name="whatsappCredits"
                                                value={formData.whatsappCredits}
                                                onChange={handleInputChange}
                                                min="0"
                                                className={`input-field ${formErrors.whatsappCredits ? 'border-error-500' : ''}`}
                                                placeholder="0"
                                            />
                                            {formErrors.whatsappCredits && (
                                                <p className="text-error-600 text-xs mt-1">{formErrors.whatsappCredits}</p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-xs text-soft-600 mb-1">
                                                SMS
                                            </label>
                                            <input
                                                type="number"
                                                name="smsCredits"
                                                value={formData.smsCredits}
                                                onChange={handleInputChange}
                                                min="0"
                                                className={`input-field ${formErrors.smsCredits ? 'border-error-500' : ''}`}
                                                placeholder="0"
                                            />
                                            {formErrors.smsCredits && (
                                                <p className="text-error-600 text-xs mt-1">{formErrors.smsCredits}</p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-xs text-soft-600 mb-1">
                                                Voice Call
                                            </label>
                                            <input
                                                type="number"
                                                name="voiceCallCredits"
                                                value={formData.voiceCallCredits}
                                                onChange={handleInputChange}
                                                min="0"
                                                className={`input-field ${formErrors.voiceCallCredits ? 'border-error-500' : ''}`}
                                                placeholder="0"
                                            />
                                            {formErrors.voiceCallCredits && (
                                                <p className="text-error-600 text-xs mt-1">{formErrors.voiceCallCredits}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-soft-700 mb-2">
                                        Credit Rates (Per Phone Number)
                                    </label>
                                    <p className="text-xs text-soft-500 mb-3">
                                        Set how many credits will be deducted per phone number when creating campaigns
                                    </p>
                                    <div className="grid grid-cols-3 gap-3">
                                        <div>
                                            <label className="block text-xs text-soft-600 mb-1">
                                                WhatsApp Rate
                                            </label>
                                            <input
                                                type="number"
                                                name="whatsappRate"
                                                value={formData.whatsappRate}
                                                onChange={handleInputChange}
                                                min="0.1"
                                                step="0.1"
                                                className={`input-field ${formErrors.whatsappRate ? 'border-error-500' : ''}`}
                                                placeholder="1"
                                            />
                                            {formErrors.whatsappRate && (
                                                <p className="text-error-600 text-xs mt-1">{formErrors.whatsappRate}</p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-xs text-soft-600 mb-1">
                                                SMS Rate
                                            </label>
                                            <input
                                                type="number"
                                                name="smsRate"
                                                value={formData.smsRate}
                                                onChange={handleInputChange}
                                                min="0.1"
                                                step="0.1"
                                                className={`input-field ${formErrors.smsRate ? 'border-error-500' : ''}`}
                                                placeholder="1"
                                            />
                                            {formErrors.smsRate && (
                                                <p className="text-error-600 text-xs mt-1">{formErrors.smsRate}</p>
                                            )}
                                        </div>

                                        <div>
                                            <label className="block text-xs text-soft-600 mb-1">
                                                Voice Call Rate
                                            </label>
                                            <input
                                                type="number"
                                                name="voiceCallRate"
                                                value={formData.voiceCallRate}
                                                onChange={handleInputChange}
                                                min="0.1"
                                                step="0.1"
                                                className={`input-field ${formErrors.voiceCallRate ? 'border-error-500' : ''}`}
                                                placeholder="1"
                                            />
                                            {formErrors.voiceCallRate && (
                                                <p className="text-error-600 text-xs mt-1">{formErrors.voiceCallRate}</p>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4 pt-4">
                                    <button
                                        type="button"
                                        onClick={handleCloseForm}
                                        className="btn-secondary w-full sm:w-auto"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        className="btn-primary flex items-center justify-center space-x-2 disabled:opacity-50 w-full sm:w-auto"
                                    >
                                        {submitting ? (
                                            <>
                                                <div className="loading-spinner"></div>
                                                <span className="hidden sm:inline">{editingUser ? 'Updating...' : 'Creating...'}</span>
                                                <span className="sm:hidden">{editingUser ? 'Updating' : 'Creating'}</span>
                                            </>
                                        ) : (
                                            <>
                                                <CheckIcon className="h-5 w-5" />
                                                <span className="hidden sm:inline">{editingUser ? 'Update User' : 'Add User'}</span>
                                                <span className="sm:hidden">{editingUser ? 'Update' : 'Add'}</span>
                                            </>
                                        )}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && userToDelete && (
                <div className="modal-overlay fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                    <div className="modal-content bg-white rounded-xl shadow-xl max-w-md w-full">
                        <div className="p-6">
                            <div className="flex items-center justify-center mb-4">
                                <div className="h-12 w-12 bg-error-100 rounded-full flex items-center justify-center">
                                    <TrashIcon className="h-6 w-6 text-error-600" />
                                </div>
                            </div>

                            <div className="text-center mb-6">
                                <h2 className="text-xl font-bold text-soft-900 mb-2">
                                    Delete User
                                </h2>
                                <p className="text-soft-600">
                                    Are you sure you want to delete user{' '}
                                    <span className="font-medium text-soft-900">"{userToDelete.name}"</span>?
                                </p>
                                <p className="text-soft-500 text-sm mt-2">
                                    This action cannot be undone and will permanently remove the user and all associated data.
                                </p>
                            </div>

                            <div className="mb-6">
                                <label className="block text-sm font-medium text-soft-700 mb-2">
                                    To confirm deletion, type <span className="font-bold text-error-600">Delete</span> in the box below:
                                </label>
                                <div className="relative">
                                    <input
                                        type="text"
                                        value={deleteConfirmText}
                                        onChange={(e) => setDeleteConfirmText(e.target.value)}
                                        className={`input-field text-center font-medium pr-10 ${deleteConfirmText === 'Delete'
                                            ? 'border-success-500 focus:ring-success-500 focus:border-success-500'
                                            : deleteConfirmText.length > 0
                                                ? 'border-error-500 focus:ring-error-500 focus:border-error-500'
                                                : ''
                                            }`}
                                        placeholder="Type 'Delete' to confirm"
                                        disabled={deleting}
                                    />
                                    {deleteConfirmText === 'Delete' && (
                                        <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                                            <CheckIcon className="h-5 w-5 text-success-500" />
                                        </div>
                                    )}
                                </div>
                                {deleteConfirmText.length > 0 && deleteConfirmText !== 'Delete' && (
                                    <p className="text-error-600 text-sm mt-1">
                                        Please type "Delete" exactly as shown
                                    </p>
                                )}
                            </div>

                            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
                                <button
                                    type="button"
                                    onClick={handleCancelDelete}
                                    disabled={deleting}
                                    className="btn-secondary disabled:opacity-50 w-full sm:w-auto"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleConfirmDelete}
                                    disabled={deleting || deleteConfirmText !== 'Delete'}
                                    className="btn-error flex items-center justify-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
                                >
                                    {deleting ? (
                                        <>
                                            <div className="loading-spinner"></div>
                                            <span>Deleting...</span>
                                        </>
                                    ) : (
                                        <>
                                            <TrashIcon className="h-5 w-5" />
                                            <span className="hidden sm:inline">Delete User</span>
                                            <span className="sm:hidden">Delete</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Disable User Confirmation Modal */}
            {showDisableModal && userToDisable && (
                <div className="modal-overlay fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
                    <div className="modal-content bg-white rounded-xl shadow-xl max-w-md w-full">
                        <div className="p-6">
                            <div className="flex items-center justify-center mb-4">
                                <div className="h-12 w-12 bg-warning-100 rounded-full flex items-center justify-center">
                                    <PowerIcon className="h-6 w-6 text-warning-600" />
                                </div>
                            </div>

                            <div className="text-center mb-6">
                                <h2 className="text-xl font-bold text-soft-900 mb-2">
                                    Disable User Access
                                </h2>
                                <p className="text-soft-600 mb-3">
                                    Are you sure you want to disable user{' '}
                                    <span className="font-medium text-soft-900">"{userToDisable.name}"</span>?
                                </p>

                                <div className="bg-warning-50 border border-warning-200 rounded-lg p-4 text-left">
                                    <h4 className="font-medium text-warning-800 mb-2">⚠️ Important:</h4>
                                    <ul className="text-sm text-warning-700 space-y-1">
                                        <li>• User will be immediately logged out</li>
                                        <li>• User cannot login until re-enabled</li>
                                        <li>• All active sessions will be terminated</li>
                                        <li>• User data and credits will be preserved</li>
                                    </ul>
                                </div>

                                <p className="text-soft-500 text-sm mt-3">
                                    You can re-enable this user at any time.
                                </p>
                            </div>

                            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4">
                                <button
                                    type="button"
                                    onClick={handleCancelDisable}
                                    disabled={disabling}
                                    className="btn-secondary disabled:opacity-50 w-full sm:w-auto"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    onClick={handleConfirmDisable}
                                    disabled={disabling}
                                    className="btn-warning flex items-center justify-center space-x-2 disabled:opacity-50 w-full sm:w-auto"
                                >
                                    {disabling ? (
                                        <>
                                            <div className="loading-spinner"></div>
                                            <span>Disabling...</span>
                                        </>
                                    ) : (
                                        <>
                                            <PowerIcon className="h-5 w-5" />
                                            <span className="hidden sm:inline">Disable User</span>
                                            <span className="sm:hidden">Disable</span>
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default UserManagement; 